/***
 * PROJECT-LANGUAGES.WIDGET
 * Select survey languages
 */

import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import { Session } from "@/redux/_session.types"
import { ProjectState } from "@/redux/project.types"
import { TemplateState } from "@/redux/template.types"
import { store } from "@/index"
import {
  projectCheckEmails,
  projectCheckLanguages,
  projectCheckMessage,
  projectCheckQrcode,
  projectEdit
} from "@/redux/project.actions"
import { sortProjectLanguages } from "@/utils/project-step-checks.utils"
import SelectLanguages from "@/components/select-languages"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLock } from "@fortawesome/free-solid-svg-icons"
import getLanguagesUtil from "@/utils/get-languages.utils"
import { useEffect, useState } from "react"
import UpgradeModal from "@/modals/upgrade.modal"
import ConfirmButtons from "@/components/confirm-buttons"
import { EmailState } from "@/redux/email.types"
import ListItem from "@/components/list-item"
import Link from "@/components/link"
import i18n from "@/translate/i18n"
import { toast } from "react-toastify"
import ProjectStepErrorsWidget from "./project-step-errors.widget"
import DuplicateTemplateAskModal from "@/modals/duplicate-template-ask.modal"
import { NavigateFunction, useNavigate } from "react-router-dom"
import Checkbox from "@/components/checkbox"
import Chip from "@/components/chip"

interface StateProps extends WithTranslation {
  _session: Session
  email: EmailState
  project: ProjectState
  template: TemplateState
}

interface OwnProps {
  onEdit: Function
  onNext: Function
}

type Props = StateProps & OwnProps

const MODAL_DUPLICATE_TEMPLATE: string = "MODAL_DUPLICATE_TEMPLATE" //Ask for duplicate
const MODAL_UPGRADE_LANGUAGES: string = "MODAL_UPGRADE_LANGUAGES" //Lock languages

function ProjectLanguagesWidget(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  //Current modal displayed
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  useEffect(() => {
    if (
      props.project.active.messageOptions.autoTranslateComment &&
      !props.project.active.messageOptions.commentPivotLanguage
    ) {
      editMessageOptionString("commentPivotLanguage", props.project.active.languages[0] || null)
    }
  }, [props.project.active.messageOptions.autoTranslateComment])

  //No language selected while the questionnaire offers only one (the others were removed from its parameters) : this one is selected
  useEffect(() => {
    const templateLanguages: string[] = props.template.active.languages || []
    if (props.project.active.languages.length === 0 && templateLanguages.length === 1) {
      editLanguages([templateLanguages[0]])
    }
  }, [props.template.active.languages, props.project.active.languages.length])

  //Set default value
  function cancel() {
    editLanguages([i18n.language])
    next(false, false)
  }

  function toogleLanguage(lang: string) {
    const languages = sortProjectLanguages(
      props.project.active.languages.indexOf(lang) > -1
        ? props.project.active.languages.filter((x: string) => x !== lang)
        : [...props.project.active.languages, lang]
    )
    editLanguages(languages)
  }

  function editLanguages(languages: string[]) {
    let newPivotLanguage = props.project.active.messageOptions.commentPivotLanguage

    // Si la langue pivot n'est plus dans la liste des langues sélectionnées, la réinitialiser
    if (newPivotLanguage && !languages.includes(newPivotLanguage)) {
      newPivotLanguage = languages.length > 0 ? languages[0] : null
    }

    // Si autoTranslateComment est activé et qu'on n'a pas de langue pivot définie, définir la première langue
    if (props.project.active.messageOptions.autoTranslateComment && !newPivotLanguage) {
      newPivotLanguage = languages.length > 0 ? languages[0] : null
    }

    const newMessageOptions = {
      ...props.project.active.messageOptions,
      commentPivotLanguage: newPivotLanguage
    }

    store.dispatch(projectEdit("languages", languages))
    store.dispatch(projectEdit("messageOptions", newMessageOptions))
    store.dispatch(projectCheckLanguages(languages))
    store.dispatch(projectCheckMessage(languages, newMessageOptions))
    store.dispatch(projectCheckQrcode(languages, props.project.active.qrcodeOptions))
    store.dispatch(projectCheckEmails(languages, props.email.list))
    props.onEdit()
  }

  function editMessageOptionBoolean(key: string, value: boolean) {
    const newOptions = {
      ...props.project.active.messageOptions,
      [key]: value
    }

    store.dispatch(projectEdit("messageOptions", newOptions))
    store.dispatch(projectCheckMessage(props.project.active.languages, newOptions))
    props.onEdit()
  }

  function editMessageOptionString(key: string, value: string | null) {
    const newOptions = {
      ...props.project.active.messageOptions,
      [key]: value
    }

    store.dispatch(projectEdit("messageOptions", newOptions))
    store.dispatch(projectCheckMessage(props.project.active.languages, newOptions))
    props.onEdit()
  }

  function editTemplate() {
    if (props.template.active.fromSupervisor) {
      setCurrentModal(MODAL_DUPLICATE_TEMPLATE)
    } else if (props.template.active.id.length) {
      navigate("/survey/template")
    } else {
      toast(t("project_error_template_no_template"), { type: "error" })
    }
  }

  function next(isVisited, isConfirmed) {
    props.onNext(isVisited, isConfirmed)
  }

  return (
    <div>
      {currentModal === MODAL_DUPLICATE_TEMPLATE && <DuplicateTemplateAskModal onClose={() => setCurrentModal(null)} />}

      {currentModal === MODAL_UPGRADE_LANGUAGES && (
        <UpgradeModal feature="languages" onClose={() => setCurrentModal(null)} />
      )}

      <ListItem>
        <div className="setup-card-help flex1">
          <p>{t("project_languages_help")}</p>
          <div className="grey-t setup-card-help-content flex">
            {t("project_languages_help_1")}
            &nbsp;
            <Link isWithoutMargin onClick={() => editTemplate()}>
              {t("template_params")}
            </Link>
          </div>
        </div>
      </ListItem>

      <div id="project-languages">
      {props._session.modules.survey.languages ? (
        <SelectLanguages
          languageIds={props.template.active.languages}
          selectedLanguageIds={props.project.active.languages}
          pivotLanguageId={props.project.active.messageOptions.commentPivotLanguage ?? undefined}
          pivotLanguageLabel={t("project_message_translation_auto_target_label_short")}
          color={props._session.accountColors.active || undefined}
          onSelect={(languageId: string) =>
            props.project.active.languages.length === 1 && props.project.active.languages[0] === languageId
              ? toast(t("at_least_one_language"), { type: "error" })
              : toogleLanguage(languageId)
          }
        />
      ) : (
        <ListItem isEditable onClick={() => setCurrentModal(MODAL_UPGRADE_LANGUAGES)}>
          <FontAwesomeIcon icon={faLock} />
          &nbsp;
          {getLanguagesUtil(props.project.active.languages)[0].fullName}
        </ListItem>
      )}
      </div>

      {/** Comments are translated by DeepL : only with the module "translate" */}
      {props._session.isTranslationAllowed && (
        <>
          <ListItem>
            <div className="setup-card-help flex1">
              <p>{t("project_message_translation_auto")}</p>
              <div className="grey-t setup-card-help-content flex">{t("project_message_translation_auto_help")}</div>
            </div>
          </ListItem>

          {props._session.modules.survey.languages ? (
            <ListItem>
              <div className="flex">
                <Checkbox
                  text={t("project_message_translation_auto")}
                  active={props.project.active.messageOptions.autoTranslateComment}
                  onClick={() =>
                    editMessageOptionBoolean(
                      "autoTranslateComment",
                      !props.project.active.messageOptions.autoTranslateComment
                    )
                  }
                />
              </div>
            </ListItem>
          ) : (
            <ListItem isEditable onClick={() => setCurrentModal(MODAL_UPGRADE_LANGUAGES)}>
              <FontAwesomeIcon icon={faLock} />
              &nbsp;
              {getLanguagesUtil(props.project.active.languages)[0].fullName}
            </ListItem>
          )}

          {props.project.active.messageOptions.autoTranslateComment && (
            <ListItem>
              <div className="setup-card-help flex1">
                <p>{t("project_message_translation_auto_target_label")}</p>
                <div className="flex flex-wrap gap-2 mt-1">
                  {props.project.active.languages.map((lang) => (
                    <Chip
                      key={lang}
                      color={
                        lang === props.project.active.messageOptions.commentPivotLanguage
                          ? props._session.accountColors.active
                          : null
                      }
                      // isOutlineColored={true}
                      isWithMargin={true}
                      onClick={() => editMessageOptionString("commentPivotLanguage", lang)}>
                      {getLanguagesUtil([lang])[0].fullName}
                    </Chip>
                  ))}
                </div>
              </div>
            </ListItem>
          )}
        </>
      )}

      <ProjectStepErrorsWidget alert={props.project.active.alerts.languages} step="languages" />

      <ConfirmButtons
        isLocked={props.project.active.alerts.languages.errors.length > 0}
        onCancel={() => cancel()}
        onConfirm={() => next(true, true)}
        onConfirmLater={() => next(true, false)}
      />

      <div className="height-20" />
    </div>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  email: state.email,
  project: state.project,
  template: state.template
})

export default connect(mapStateToProps)(withTranslation()(ProjectLanguagesWidget))
